import { useMemo, useState, useCallback } from "react";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { useUserState } from "@context/UserContext";

import {
  INotificationService,
  IUserContextForNotification,
  IUserNotification,
} from "@modules/notifications/model/INotifications";

import { Types } from "@ioc/types";

import { IHookNotificationPortal } from "../model/inteface.notificationPortal";

const CENTER_DEFAULT_BY_TESTS_C001 = "C001";
const CENTER_DEFAULT_BY_TESTS_C003 = "C003";

const useNotificationPortalInternal = (): IHookNotificationPortal => {
  const iocContext = useIoCContext();
  const userState = useUserState();
  const { enqueueSnackbar } = useSnackbar();

  const service = useMemo(
    () =>
      iocContext.serviceContainer.get<INotificationService>(
        Types.Notifications.INotificationService
      ),
    [iocContext.serviceContainer]
  );

  const [open, setOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<IUserNotification[]>([]);
  const [
    currentNotification,
    setCurrentNotification,
  ] = useState<IUserNotification | null>(null);

  const buildUserParams = (): IUserContextForNotification => ({
    name: userState?.username ?? "",
    email: userState?.email ?? "",
    /* NOTE: AINDA NAO EXISTE ROTA PARA ATUALIZAR CENTROS VIA RBAC, NECESSARIO SETAR MOCKADO PARA FINS DE TESTES */
    /* NOTE: APOS EXISTENCIA DA ROTA, INTEGRAR E SUBSTITUIR POR -> userState?.centers?.map((center) => center.toString())  */
    /* NOTE: Por enquanto, realizar testes com C001 e C003  */
    centers: [CENTER_DEFAULT_BY_TESTS_C003, CENTER_DEFAULT_BY_TESTS_C001], // NOTE: REPLACE REQUIRED !
    segments: [], // TODO: mapear quando tiver segments reais
    profiles: ["Cliente"],
  });

  const handleClose = () => {
    setCurrentNotification(null);
    setOpen(false);
  };

  const hasNextNotification = useMemo(
    () =>
      notifications.some(
        (item) => !item.isRead && item.id !== currentNotification?.id
      ),
    [notifications, currentNotification?.id]
  );

  const reopen = (uuid) => {
    // só reabre se ainda existir alguma notificação carregada
    if (notifications.length > 0) {
      const find =
        notifications.find((item) => item.id === Number(uuid)) ?? null;
      setCurrentNotification(find);
      setOpen(true);
    }
  };

  const getNotificationsReadUnread = useCallback(
    async () => {
      try {
        const ctx = buildUserParams();
        return await service.listForUserWithReadStatus(ctx);
      } catch (error) {
        const errorMessageRead = "Erro ao buscar notificações do portal";
        enqueueSnackbar(errorMessageRead, { variant: "error" });
        return [];
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service]
  );

  const markCurrentAsRead = async () => {
    if (!currentNotification) return;
    const ctx = buildUserParams();
    await service.markRead(currentNotification.id, ctx);
  };

  const handleNext = async () => {
    if (!currentNotification) return;

    // 1) marca a atual como lida no backend
    await markCurrentAsRead();

    // 2) busca novamente a lista já atualizada (lido/não lido)
    const active = await getNotificationsReadUnread();

    if (active.length > 0) {
      setNotifications(active);

      // 3) encontra a próxima não lida (qualquer uma)
      const nextNotification = active.find((item) => !item.isRead) ?? null;

      if (nextNotification) {
        setCurrentNotification(nextNotification);
        setOpen(true);
        return;
      }
    }

    // 4) se não tiver próxima não lida, fecha o modal
    setCurrentNotification(null);
    setOpen(false);
  };

  const messageReadCount = useCallback(() => {
    return notifications?.filter((item) => item?.isRead === false).length;
  }, [notifications]);

  const autoFetchGetNotificationsReadUnread = useCallback(async () => {
    const active = await getNotificationsReadUnread();

    if (active.length > 0) {
      setNotifications(active);

      const firstUnread = active.find((n) => !n.isRead) ?? null;
      setCurrentNotification(firstUnread);
    } else {
      setNotifications([]);
      setCurrentNotification(null);
      setOpen(false);
    }
  }, [getNotificationsReadUnread]);

  return {
    state: {
      open,
      notifications,
      currentNotification,
      hasNext: hasNextNotification,
      hasPortalNotification: notifications.length > 0,
      messageReadCount: messageReadCount(),
    },
    action: {
      handleClose,
      handleNext,
      reopen,
      autoFetchGetNotificationsReadUnread,
    },
  };
};

export default useNotificationPortalInternal;
